import { Button, CircularProgress, FormControl, IconButton, Stack, TextField } from '@mui/material';
import { EyeClosedIcon, EyeIcon } from '@phosphor-icons/react';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi, Navigate } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

import { ButtonLink } from '@/components/router/ButtonLink';
import { JwtTokenType } from '@/modules/api/backend';
import {
  backendApiAuthenticationLoginMutation,
  backendApiAuthenticationRefreshOptions,
} from '@/modules/api/backend/@tanstack/react-query.gen';

const route = getRouteApi('/_public/_authentication/login');

export function Login() {
  const navigate = route.useNavigate();
  const { redirect } = route.useSearch();
  const [showPassword, setShowPassword] = useState(false);

  const { mutate, isError } = useMutation(backendApiAuthenticationLoginMutation());

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: ({ value }) => {
      mutate(
        { body: value },
        {
          onSuccess: async () => {
            await navigate({ href: redirect ?? '/', replace: true });
          },
        },
      );
    },
  });

  const { isSuccess, isLoading } = useQuery({
    ...backendApiAuthenticationRefreshOptions({ body: { type: JwtTokenType.ACCESS } }),
    retry: false,
  });

  return (
    <Fragment>
      {isSuccess || isLoading ? (
        <Stack alignItems="center" paddingY={14}>
          <CircularProgress color="primary" size="lg" />
          {/* @ts-expect-error Navigate href prop is untyped */}
          {isSuccess ? <Navigate replace href={redirect ?? '/'} /> : null}
        </Stack>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <Stack spacing={2}>
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => (!value ? 'Erforderlich' : false),
              }}
            >
              {(field) => (
                <FormControl error={isError || field.state.meta.errors.length > 0} sx={{ marginBottom: 3 }}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="E-Mail"
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    type="email"
                    value={field.state.value}
                  />
                </FormControl>
              )}
            </form.Field>

            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) => (!value ? 'Erforderlich' : false),
              }}
            >
              {(field) => (
                <FormControl error={isError || field.state.meta.errors.length > 0} sx={{ marginBottom: 3 }}>
                  <TextField
                    autoFocus
                    fullWidth
                    error={Boolean(isError) || field.state.meta.errors.length > 0}
                    helperText={field.state.meta.errors[0] ?? (isError ? 'Falsches Passwort oder Benutzername' : '')}
                    label="Passwort"
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    value={field.state.value}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPassword((prevState) => !prevState)}
                            title={showPassword ? 'Verstecken' : 'Anzeigen'}
                          >
                            {showPassword ? <EyeClosedIcon /> : <EyeIcon />}
                          </IconButton>
                        ),
                      },
                    }}
                  />
                </FormControl>
              )}
            </form.Field>

            <form.Subscribe selector={(formState) => [formState.canSubmit, formState.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Stack alignItems="center" direction="row" gap={3} justifyContent="flex-end">
                  <ButtonLink color="primary" to="/password-reset" variant="outlined">
                    Passwort vergessen
                  </ButtonLink>
                  <Button disabled={!canSubmit} type="submit" variant="contained">
                    {isSubmitting ? <CircularProgress /> : 'Anmelden'}
                  </Button>
                </Stack>
              )}
            </form.Subscribe>
          </Stack>
        </form>
      )}
    </Fragment>
  );
}
